import React from 'react';
import { Link } from 'react-router-dom';
import {
    FaWhatsapp,
    FaEnvelope,
    FaPhoneAlt,
    FaMapMarkerAlt,
    FaFileUpload,
    FaPaperPlane,
    FaCheckCircle,
    FaArrowRight,
    FaShieldAlt,
    FaClock,
    FaFax
} from 'react-icons/fa';
import Navbar from '../Common/Navbar'; // adjust path if your Navbar file lives elsewhere
import Footer from '../Common/Footer';
import { APP_CONFIG } from '../../config/app.config';
import './CraFax.css';

const steps = [
    {
        icon: <FaFileUpload size={20} />,
        title: 'Send us your document',
        desc: 'Share the file you need faxed, however works for you — WhatsApp, email, or upload.'
    },
    {
        icon: <FaPaperPlane size={20} />,
        title: 'We transmit it to CRA',
        desc: 'Your document goes straight to the correct CRA fax line, no manual dialing required.'
    },
    {
        icon: <FaCheckCircle size={20} />,
        title: 'You get confirmation',
        desc: 'We send you a transmission receipt the moment it lands successfully.'
    },
];

const trustPoints = [
    { icon: <FaClock size={16} />, label: 'Same-day transmission' },
    { icon: <FaShieldAlt size={16} />, label: 'Confidential handling' },
    { icon: <FaCheckCircle size={16} />, label: 'Delivery confirmation' },
];

const CraFax = () => {
    return (
        <>
            <div className="cra_fax_container">
                <div className="cra_fax_bg">
                    <Navbar />
                    <div className="cra_fax_head_container">
                        <div className="cf-eyebrow mb-3">
                            <FaFax size={12} /> CRA Fax Service
                        </div>
                        <h1>Send your fax to CRA, faster</h1>
                        <p className="cra_fax_head_subtitle">
                            We help you send documents to the CRA quickly and reliably —
                            no fax machine, no busy signals, no trip to a print shop.
                        </p>
                    </div>
                </div>
            </div>

            <div className="cf-page">
                {/* Introduction & Trust badge section */}
                <div className="cf-section pb-0">
                    <div className="container">
                        <div className="cf-cta-container p-4 p-md-5 mx-auto text-center">
                            <h3 className="fw-bold mb-3" style={{ color: 'var(--cf-navy)' }}>
                                Direct & Secure Transmission
                            </h3>
                            <p className="text-muted mx-auto mb-4" style={{ maxWidth: '600px', fontSize: '0.95rem' }}>
                                Skip the print shop and busy lines. Send your official documents straight to the correct CRA fax receiver queue, handled confidentially and securely.
                            </p>
                            
                            <div className="d-flex gap-3 justify-content-center flex-wrap mb-4">
                                <a
                                    href={APP_CONFIG.whatsAppLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn cf-btn-whatsapp d-inline-flex align-items-center"
                                >
                                    <FaWhatsapp className="me-2" size={18} /> Chat on WhatsApp
                                </a>
                                <Link to="/contact" className="btn cf-btn-outline d-inline-flex align-items-center">
                                    Contact us for details <FaArrowRight className="ms-2" size={14} />
                                </Link>
                            </div>

                            <div className="cf-trust-badge-strip">
                                {trustPoints.map((point, idx) => (
                                    <span className="cf-trust-badge" key={idx}>
                                        {point.icon} {point.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* How it works */}
                <div className="cf-section">
                    <div className="container">
                        <div className="text-center mb-5">
                            <h2 className="cf-section-title mb-2">Three steps, that's it</h2>
                            <p className="text-muted">No fax machine, no waiting in line, no confusion.</p>
                        </div>
                        <div className="row g-4 align-items-stretch">
                            {steps.map((step, idx) => (
                                <React.Fragment key={idx}>
                                    <div className="col-12 col-md-3">
                                        <div className="cf-step-card">
                                            <span className="cf-step-num">{`0${idx + 1}`}</span>
                                            <div className="cf-step-icon">{step.icon}</div>
                                            <h6 className="fw-bold mb-2" style={{ color: 'var(--cf-navy)' }}>{step.title}</h6>
                                            <p className="text-muted small mb-0">{step.desc}</p>
                                        </div>
                                    </div>
                                    {idx < steps.length - 1 && (
                                        <div className="col-12 col-md-1 cf-arrow d-none d-md-flex">
                                            <FaArrowRight size={18} />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Contact */}
                <div className="cf-section pt-0">
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-lg-6">
                                <div className="cf-contact-card p-4 p-md-5">
                                    <h4 className="fw-bold mb-4" style={{ color: 'var(--cf-navy)' }}>Direct Contact</h4>

                                    {/* WhatsApp Card Highlight */}
                                    <div className="cf-whatsapp p-4 mb-4">
                                        <div className="d-flex align-items-center gap-3 mb-2">
                                            <FaWhatsapp size={30} />
                                            <h5 className="fw-bold mb-0">Chat on WhatsApp</h5>
                                        </div>
                                        <p className="mb-3 opacity-75 small">
                                            Fastest response times for quick queries and support.
                                        </p>
                                        <a
                                            href={APP_CONFIG.whatsAppLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-light fw-bold w-100 text-dark"
                                        >
                                            Message Us Now
                                        </a>
                                    </div>

                                    <div className="cf-contact-row">
                                        <div className="cf-contact-icon"><FaEnvelope size={16} /></div>
                                        <div>
                                            <h6 className="fw-bold mb-1">Email</h6>
                                            <a href={APP_CONFIG.emailLink} className="text-muted small">{APP_CONFIG.email}</a>
                                        </div>
                                    </div>

                                    <div className="cf-contact-row">
                                        <div className="cf-contact-icon"><FaPhoneAlt size={16} /></div>
                                        <div>
                                            <h6 className="fw-bold mb-1">Phone</h6>
                                            <a href={APP_CONFIG.phoneLink} className="text-muted small">{APP_CONFIG.phone}</a>
                                        </div>
                                    </div>

                                    <div className="cf-contact-row mb-0">
                                        <div className="cf-contact-icon"><FaMapMarkerAlt size={16} /></div>
                                        <div>
                                            <h6 className="fw-bold mb-1">Office Location</h6>
                                            <p className="text-muted small mb-0">{APP_CONFIG.address}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
}

export default CraFax;